/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TextField from "../../inputs/TextField";
import NumberField from "../../inputs/NumberField";
import ToggleField from "../../inputs/ToggleField";
import TabPanel from "../../../ui/tabs/TabPanel";
import Form from "../../../ui/Form/Form";
import { ADD_XSSI_CONNECTION_FORM_FIELDS } from "../../../constants";
import Button from "../../buttons/Button";
import AuthContext from "../../../store/authContext";
import { errorAlert, makeApiRequest } from "../../../util";
import OrgContext from "../../../store/orgContext";
import DeleteModal, { DeleteModalIcons } from "../../modal/DeleteModal";
import SuccessAnimation from "../../../ui/animation/SuccessAnimation";
import HexInput from "../../../ui/HexInput";
import {
  CREATE_XSSI_CONNECTION_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../../translations/util";
import MultiselectSearchField from "../../inputs/MultiselectSearchField";
import ESOganisationDropdown from "../../dropdown/esOrganisationDropdown";
import DropdownSearchField from "../../inputs/DropdownSearchField";
import ReuseConnection from "./ReuseConnection";

const set = new Set();
interface IProps {
  setNewRef: any;
}

interface IConnectionData {
  "CONNECTION-ID": string;
  "RADIO-IPV4-ADDR": string;
  "RADIO-RFSS-ID"?: number;
  "RADIO-SYSTEM-ID"?: number;
  "RADIO-WACN-ID"?: number;
  "RADIO-RFSS-4-HEX-DIGIT": boolean;
  "IWF-RFSS-ID"?: number;
  "IWF-SYSTEM-ID"?: number;
  "IWF-WACN-ID"?: number;
  "RESPONSES-BEFORE-CONNECTED": number;
  "MISSED-RESPONSES-BEFORE-DISCONNECTED": number;
  "VOCODER-MODES": string[];
  "POLLING-ENABLED": boolean;
  "POLLING-INTERVAL-CONNECTED": number;
  "POLLING-INTERVAL-DISCONNECTED": number;
}

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_XSSI_CONNECTION_FORM_FIELDS.map(
    (rows) => {
      const res = rows.fields.map((field) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "MULTISELECT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
          case "HEX":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          default:
            return false;
        }
      });

      return res.reduce((acc, el) => acc || el);
    }
  ).reduce((acc, el) => acc || el);

  return isConnectionFormInvalid;
};

let newConnectionRef: string | null = null;

const INITIAL_STATE = {
  "CONNECTION-ID": "",
  "RADIO-IPV4-ADDR": "",
  "RADIO-RFSS-ID": undefined,
  "RADIO-SYSTEM-ID": undefined,
  "RADIO-WACN-ID": undefined,
  "RADIO-RFSS-4-HEX-DIGIT": true,
  "IWF-RFSS-ID": undefined,
  "IWF-SYSTEM-ID": undefined,
  "IWF-WACN-ID": undefined,
  "RESPONSES-BEFORE-CONNECTED": 1,
  "MISSED-RESPONSES-BEFORE-DISCONNECTED": 3,
  "VOCODER-MODES": [],
  "POLLING-ENABLED": true,
  "POLLING-INTERVAL-CONNECTED": 10,
  "POLLING-INTERVAL-DISCONNECTED": 10,
};

const AddConnection = ({ setNewRef }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const { selectedOrg, orgs: orgList } = useContext(OrgContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isConnectionReused, setIsConnectionReused] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<{
    id: string;
    text: string;
  } | null>({ id: "", text: "" });
  const [reusedConnectionId, setReusedConnectionId] = useState("");
  const [xssiConnectionIds, setXssiConnectionIds] = useState<string[]>([]);

  const [connectionData, setConnectionData] =
    useState<IConnectionData>(INITIAL_STATE);
  const [reusedConnectionData, setReusedConnectionData] =
    useState<IConnectionData>(INITIAL_STATE);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [noResultFound, setNoResultFound] = useState(false);

  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const fetchXSSIConnections = useCallback(async () => {
    setLoading(true);
    setNoResultFound(false);
    try {
      if (selectedOrgId) {
        const queryParams = new URLSearchParams({
          page: `1`,
          pageSize: `100`,
          ...(reusedConnectionId && {
            "CONNECTION-ID": reusedConnectionId,
          }),
        });

        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<IConnectionData>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/xssi-connections?${queryParams}`,
          token,
          org: selectedOrgId?.id,
        });
        if (response.NbrOfRows > 0) {
          if (isConnectionReused && selectedOrgId) {
            const result: any[] = response.Rows.map(
              (row) => row["CONNECTION-ID"]
            );
            setXssiConnectionIds(result);
            if (reusedConnectionId) {
              setReusedConnectionData({
                ...reusedConnectionData,
                "CONNECTION-ID": reusedConnectionId,
                "RADIO-IPV4-ADDR": response.Rows[0]["RADIO-IPV4-ADDR"],
                "RADIO-RFSS-ID": response.Rows[0]["RADIO-RFSS-ID"],
                "RADIO-SYSTEM-ID": response.Rows[0]["RADIO-SYSTEM-ID"],
                "RADIO-WACN-ID": response.Rows[0]["RADIO-WACN-ID"],
                "IWF-RFSS-ID": response.Rows[0]["IWF-RFSS-ID"],
                "IWF-SYSTEM-ID": response.Rows[0]["IWF-SYSTEM-ID"],
                "IWF-WACN-ID": response.Rows[0]["IWF-WACN-ID"],
                "POLLING-ENABLED": response.Rows[0]["POLLING-ENABLED"],
                "POLLING-INTERVAL-CONNECTED":
                  response.Rows[0]["POLLING-INTERVAL-CONNECTED"],
                "POLLING-INTERVAL-DISCONNECTED":
                  response.Rows[0]["POLLING-INTERVAL-DISCONNECTED"],
                "RADIO-RFSS-4-HEX-DIGIT":
                  response.Rows[0]["RADIO-RFSS-4-HEX-DIGIT"],
                "MISSED-RESPONSES-BEFORE-DISCONNECTED":
                  response.Rows[0]["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
                "VOCODER-MODES": response.Rows[0]["VOCODER-MODES"],
                "RESPONSES-BEFORE-CONNECTED":
                  response.Rows[0]["RESPONSES-BEFORE-CONNECTED"],
              });
            }
          }
        } else {
          setNoResultFound(true);
          setXssiConnectionIds([]);
          setReusedConnectionData(INITIAL_STATE);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reusedConnectionId, selectedOrgId, token]);

  const newConnectionBody = JSON.stringify({
    "CONNECTION-ID": connectionData["CONNECTION-ID"],
    "RADIO-IPV4-ADDR": connectionData["RADIO-IPV4-ADDR"],
    "RADIO-RFSS-ID": connectionData["RADIO-RFSS-ID"],
    "RADIO-SYSTEM-ID": connectionData["RADIO-SYSTEM-ID"],
    "RADIO-WACN-ID": connectionData["RADIO-WACN-ID"],
    "RADIO-RFSS-4-HEX-DIGIT": connectionData["RADIO-RFSS-4-HEX-DIGIT"],
    "IWF-RFSS-ID": connectionData["IWF-RFSS-ID"],
    "IWF-SYSTEM-ID": connectionData["IWF-SYSTEM-ID"],
    "IWF-WACN-ID": connectionData["IWF-WACN-ID"],
    "RESPONSES-BEFORE-CONNECTED": connectionData["RESPONSES-BEFORE-CONNECTED"],
    "MISSED-RESPONSES-BEFORE-DISCONNECTED":
      connectionData["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
    "VOCODER-MODES": connectionData["VOCODER-MODES"],
    "POLLING-ENABLED": connectionData["POLLING-ENABLED"],
    "POLLING-INTERVAL-CONNECTED": connectionData["POLLING-INTERVAL-CONNECTED"],
    "POLLING-INTERVAL-DISCONNECTED":
      connectionData["POLLING-INTERVAL-DISCONNECTED"],
  });

  const reusedConnectionBody = JSON.stringify({
    "CONNECTION-ID": reusedConnectionData["CONNECTION-ID"],
    "RADIO-IPV4-ADDR": reusedConnectionData["RADIO-IPV4-ADDR"],
    "RADIO-RFSS-ID": reusedConnectionData["RADIO-RFSS-ID"],
    "RADIO-SYSTEM-ID": reusedConnectionData["RADIO-SYSTEM-ID"],
    "RADIO-WACN-ID": reusedConnectionData["RADIO-WACN-ID"],
    "RADIO-RFSS-4-HEX-DIGIT": reusedConnectionData["RADIO-RFSS-4-HEX-DIGIT"],
    "IWF-RFSS-ID": reusedConnectionData["IWF-RFSS-ID"],
    "IWF-SYSTEM-ID": reusedConnectionData["IWF-SYSTEM-ID"],
    "IWF-WACN-ID": reusedConnectionData["IWF-WACN-ID"],
    "RESPONSES-BEFORE-CONNECTED":
      reusedConnectionData["RESPONSES-BEFORE-CONNECTED"],
    "MISSED-RESPONSES-BEFORE-DISCONNECTED":
      reusedConnectionData["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
    "VOCODER-MODES": reusedConnectionData["VOCODER-MODES"],
    "POLLING-ENABLED": reusedConnectionData["POLLING-ENABLED"],
    "POLLING-INTERVAL-CONNECTED":
      reusedConnectionData["POLLING-INTERVAL-CONNECTED"],
    "POLLING-INTERVAL-DISCONNECTED":
      reusedConnectionData["POLLING-INTERVAL-DISCONNECTED"],
  });

  const createXSSIConnections = async () => {
    setLoading(true);
    try {
      const response = await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connections`,
        method: "POST",
        body: isConnectionReused ? reusedConnectionBody : newConnectionBody,
        token,
        org: selectedOrg?.id,
      });
      newConnectionRef = response.Ref;
      setShowSuccessAnimation(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrgId?.id) {
      fetchXSSIConnections();
    } else {
      setReusedConnectionId("");
      if (noResultFound) setNoResultFound(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchXSSIConnections, selectedOrgId?.id]);

  useEffect(() => {
    if (reusedConnectionId) {
      fetchXSSIConnections();
    } else {
      setReusedConnectionData({ ...INITIAL_STATE });
    }
  }, [fetchXSSIConnections, reusedConnectionId]);

  useEffect(() => {
    if (isConnectionReused) {
      setConnectionData(INITIAL_STATE);
    } else {
      setXssiConnectionIds([]);
      setSelectedOrgId({ id: "", text: "" });
      setReusedConnectionId("");
      setReusedConnectionData(INITIAL_STATE);
    }
  }, [isConnectionReused]);

  useEffect(() => {
    if (debouncedTerm) setReusedConnectionId(debouncedTerm);
  }, [debouncedTerm]);

  useEffect(() => {
    let timeoutID: string | number | NodeJS.Timeout | undefined;
    if (searchTerm) {
      timeoutID = setTimeout(() => {
        setDebouncedTerm(searchTerm);
      }, 800);
    }
    return () => clearTimeout(timeoutID);
  }, [searchTerm]);

  return (
    <div className="data-tabcontent">
      {showSuccessAnimation ? (
        <SuccessAnimation
          onComplete={() => {
            setNewRef(newConnectionRef);
            setShowSuccessAnimation(false);
          }}
          msg={
            CREATE_XSSI_CONNECTION_TRANSLATIONS.addConnection
              .successAlertMessage
          }
        />
      ) : (
        <TabPanel>
          <div className="data-form">
            <Form>
              <div className="data-tabcontent data-card">
                <ToggleField
                  labelText={
                    CREATE_XSSI_CONNECTION_TRANSLATIONS.reuseXSSIConnection
                      .reuseToggleLabel
                  }
                  onChange={(val) => setIsConnectionReused(val)}
                  value={isConnectionReused}
                  tooltipText={
                    CREATE_XSSI_CONNECTION_TRANSLATIONS.reuseXSSIConnection
                      .reuseToggleTooltip
                  }
                  defaultToggled
                />

                {isConnectionReused ? (
                  <div className="grid grid-cols-2 gap-4 form-control">
                    <div className="reuse-xssi-org-dropdown">
                      <ESOganisationDropdown
                        org={selectedOrgId || undefined}
                        orgsList={orgList.filter(
                          (org) => org.id !== selectedOrg?.id
                        )}
                        setXSSIOrg={setSelectedOrgId}
                        className="dropdown-field"
                      />
                    </div>
                    <div>
                      <DropdownSearchField
                        className="dropdown-field xssi-connection-dropdown"
                        label={
                          CREATE_XSSI_CONNECTION_TRANSLATIONS
                            .reuseXSSIConnection.reuseableXSSIConnectionLabel
                        }
                        placeholder={
                          CREATE_XSSI_CONNECTION_TRANSLATIONS
                            .reuseXSSIConnection.reuseableXSSIConnectionLabel
                        }
                        items={xssiConnectionIds}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string;
                        }) => {
                          if (selectedOrgId?.id) {
                            setReusedConnectionId(selectedItem);
                          }
                        }}
                        selectedItem={reusedConnectionId}
                        tooltipText={
                          CREATE_XSSI_CONNECTION_TRANSLATIONS
                            .reuseXSSIConnection.reuseableXSSIConnectionTooltip
                        }
                        required
                        disabled={!selectedOrgId}
                        setSearchTerm={setSearchTerm}
                        warn={noResultFound}
                        warnText={
                          CREATE_XSSI_CONNECTION_TRANSLATIONS
                            .reuseXSSIConnection.noMatchFound
                        }
                      />
                    </div>
                    <ReuseConnection
                      setReusedConnectionData={(
                        value: React.SetStateAction<IConnectionData>
                      ) => setReusedConnectionData(value)}
                      reusedConnectionData={reusedConnectionData}
                      reusedConnectionId={reusedConnectionId}
                      isConnectionReused={isConnectionReused}
                      isInValidNumberForField={isInValidNumberForField}
                    />
                  </div>
                ) : (
                  <>
                    {ADD_XSSI_CONNECTION_FORM_FIELDS.map((rows) => {
                      return (
                        <div
                          className={`grid grid-cols-${rows.cols} gap-4 form-control`}
                        >
                          {rows.fields.map((field) => {
                            const { name } = field as {
                              name: keyof IConnectionData;
                            };
                            switch (field.type) {
                              case "TOGGLE":
                                return (
                                  <ToggleField
                                    labelText={field.label}
                                    onChange={(val) =>
                                      setConnectionData({
                                        ...connectionData,
                                        [field.name]: val,
                                      })
                                    }
                                    value={connectionData[name] as boolean}
                                    tooltipText={field.tooltipText}
                                    defaultToggled
                                  />
                                );
                              case "NUMBER":
                                return (
                                  <NumberField
                                    onChange={(val) => {
                                      set.add(field.name);
                                      if (!val || !Number.isNaN(+val))
                                        setConnectionData({
                                          ...connectionData,
                                          [field.name]: val ? +val : undefined,
                                        });
                                    }}
                                    label={field.label}
                                    placeholder={field.label}
                                    max={field.max || 1}
                                    min={field.min || 1}
                                    invalid={
                                      set.has(field.name) &&
                                      connectionData[name] !== 0 &&
                                      !connectionData[name]
                                    }
                                    value={connectionData[name] as number}
                                    invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                                    tooltipText={field.tooltipText}
                                    required
                                  />
                                );
                              case "HEX":
                                return (
                                  <HexInput
                                    onChange={(val) => {
                                      set.add(field.name);
                                      setConnectionData({
                                        ...connectionData,
                                        [field.name]:
                                          val || val === 0 ? +val : undefined,
                                      });
                                    }}
                                    label={field.label}
                                    placeholder={field.label}
                                    max={field.max || 1}
                                    min={field.min || 1}
                                    invalid={
                                      set.has(field.name) &&
                                      isInValidNumberForField(
                                        connectionData[name] as number,
                                        field.min,
                                        field.max
                                      )
                                    }
                                    value={connectionData[name] as number}
                                    invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                                    tooltipText={field.tooltipText}
                                    required
                                  />
                                );
                              case "MULTISELECT":
                                return (
                                  <MultiselectSearchField
                                    className="dropdown-field"
                                    titleText={field.label}
                                    items={[
                                      { id: "FR", text: "FR" },
                                      { id: "HR", text: "HR" },
                                      { id: "Native", text: "Native" },
                                      { id: "Phase 1", text: "Phase 1" },
                                    ]}
                                    onChange={({
                                      selectedItems,
                                    }: {
                                      selectedItems: {
                                        id: string;
                                        text: string;
                                      }[];
                                    }) =>
                                      setConnectionData({
                                        ...connectionData,
                                        [field.name]: selectedItems.map(
                                          (item) => item.text
                                        ),
                                      })
                                    }
                                    tooltipText={field.tooltipText}
                                    required
                                    disabled={false}
                                  />
                                );
                              default:
                                return (
                                  <TextField
                                    onChange={(val) => {
                                      set.add(field.name);
                                      setConnectionData({
                                        ...connectionData,
                                        [field.name]: val,
                                      });
                                    }}
                                    placeholder={field.label}
                                    value={connectionData[name] as string}
                                    invalid={
                                      set.has(field.name) &&
                                      field.isInputInvalid?.(
                                        connectionData[name] as string
                                      )
                                    }
                                    invalidText={
                                      field.invalidText ||
                                      FORM_VALIDATION_TRANSLATIONS.required(
                                        field.label
                                      )
                                    }
                                    labelText={field.label}
                                    tooltipText={field.tooltipText}
                                    required
                                  />
                                );
                            }
                          })}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </Form>
          </div>

          <DeleteModal
            modalHeaderText={
              CREATE_XSSI_CONNECTION_TRANSLATIONS.addConnection.deleteModal
                .header
            }
            modalDescriptionText={
              CREATE_XSSI_CONNECTION_TRANSLATIONS.addConnection.deleteModal
                .description
            }
            onDelete={() =>
              navigate("/configurationManagement/xssiconnections")
            }
            open={open}
            setOpen={setOpen}
            confirmationButtonLabel={
              CREATE_XSSI_CONNECTION_TRANSLATIONS.addConnection.deleteModal
                .goBackButtonLabel
            }
            cancelIcon={DeleteModalIcons.Cancel}
          />

          <div className="flex justify-end form-footer">
            <div className="mr-2">
              <Button
                className="w-full"
                type="button"
                kind="tertiary"
                disabled={false}
                label={
                  CREATE_XSSI_CONNECTION_TRANSLATIONS.addConnection.btnLabels
                    .cancel
                }
                onClick={() => setOpen(true)}
                dataCy="cancelCreateXSSI-"
              />
            </div>

            <div>
              <Button
                className="w-full"
                type="button"
                loading={loading}
                disabled={
                  isConnectionReused && reusedConnectionId
                    ? isFormInvalid({ data: reusedConnectionData })
                    : isFormInvalid({ data: connectionData })
                }
                onClick={createXSSIConnections}
                label={
                  CREATE_XSSI_CONNECTION_TRANSLATIONS.addConnection.btnLabels
                    .createConnection
                }
                dataCy="createXSSI-"
              />
            </div>
          </div>
        </TabPanel>
      )}
    </div>
  );
};

export default AddConnection;
